import React, { useState } from "react";
import { CloseIcon } from "../../assets/icons";
import { DropdownComponent, InputComponent } from "../../components/core/Forms";
import { useProjectStore } from "../../state/useProjectManagement";
import AddNewCustomerDesktop from "../../components/Invoice/sections/customer/desktopAddCustomer";
import { ICustomer, IProject, ProjectStatus } from "../../@types";
import AddNewCustomer from "../../components/Invoice/sections/customer/AddNewCustomer";
import SelectCustomer from "../../components/Invoice/sections/customer/SelectCustomer";
import { showToast } from "../../utils/toastUtils";
import { createProject } from "../../backend-services/project";

interface Props {
  onClose: () => void;
  addProject?: (id: string) => Promise<void>;
  isOpen?: boolean;
  setSelectedProject?: (project: IProject) => void;
  selectedCustomer?: ICustomer;
  refetch?: () => void;
}

type ProjectFormData = {
  name: string;
  customerId: string;
  startDate: string;
  endDate: string;
  notes: string;
  status: ProjectStatus;
};

const CreateProject = ({
  onClose,
  addProject,
  isOpen: isModalOpen,
  setSelectedProject,
  selectedCustomer: selectedProjectCustomer,
  refetch,
}: Props) => {
  const [modalInView, setModalInView] = React.useState("select_customer");
  const [isOpen, setIsOpen] = React.useState(false);
  const [, setCustomerFormData] = React.useState<ICustomer>({} as ICustomer);
  const [selectedCustomer, setSelectedCustomer] = useState<ICustomer | null>();
  const [isLoading, setIsLoading] = React.useState(false);
  const [formData, setFormData] = useState<ProjectFormData>({
    name: "",
    customerId: selectedProjectCustomer ? selectedProjectCustomer.id : "",
    startDate: new Date().toISOString(),
    endDate: "",
    notes: "",
    status: "not-started" as ProjectStatus,
  });
  const [error, setError] = useState<ProjectFormData>({
    name: "",
    customerId: "",
    startDate: "",
    endDate: "",
    notes: "",
    status: "" as ProjectStatus,
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setError({ ...error, [name]: "" });
    if (name == "startDate" || name == "endDate") {
      setFormData({ ...formData, [name]: new Date(value).toISOString() });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSetCustomerWrapper = (selectedCustomer: ICustomer | null) => {
    setError({ ...error, customerId: "" });
    setFormData({ ...formData, customerId: selectedCustomer?.id! });
  };

  const handleClose = () => {
    setIsOpen(false);
    setModalInView("select_customer");
  };

  const fieldLabels: Partial<Record<keyof ProjectFormData, string>> = {
    name: "Project Name",
    customerId: "Customer",
    startDate: "Start Date",
    endDate: "End Date",
  };

  const validateFields = (): boolean => {
    const required: (keyof ProjectFormData)[] = [
      "name",
      "customerId",
      "startDate",
      "endDate",
    ];

    for (const field of required) {
      if (!formData[field]) {
        setError((prev) => ({
          ...prev,
          [field]: `${fieldLabels[field] || field} is required.`,
        }));
        return false;
      }
    }
    return true;
  };

  const handleCreateProject = async () => {
    if (!validateFields()) return;
    setIsLoading(true);
    try {
      const res = await createProject({
        ...formData,
      });
      showToast("Project created successfully!", "success");
      setIsLoading(false);
      // @ts-ignore
      addProject?.(res?.id);
      setSelectedProject?.(res as unknown as IProject);
      onClose?.();
      refetch?.();
    } catch (error: any) {
      setIsLoading(false);
      console.log(error);
      showToast(
        error.message || "An error occurred while creating the customer.",
        "error",
      );
    }
  };
  if (!isModalOpen) return null;
  return (
    <>
      {/* Overlay to close the sidebar */}
      <div
        className="fixed inset-0 bg-black bg-opacity-30 z-40 backdrop-blur-sm "
        onClick={onClose}
      ></div>

      {/* Sidebar Modal */}
      <div className="fixed top-0 right-0 h-full w-[90%] md:w-[35%] bg-white shadow-lg z-50 transform transition-transform duration-300  ease-in-out overflow-y-auto animate-slide-in-right">
        <div className="relative flex flex-col justify-between h-full p-6">
          <div>
            {/* Header */}
            <div className="flex items-center mt-5 justify-between">
              <h1 className="text-[20px] font-medium leading-6">
                Create Project
              </h1>

              <button
                className=" flex justify-center items-center mx-3  text-black hover:text-gray-900"
                onClick={onClose}
              >
                <CloseIcon className="cursor-pointer" />
              </button>
            </div>

            {/* FORM */}
            <form className="mt-10 flex flex-col gap-5">
              <div className="mt-5 md:mt-0 w-full">
                <div className="flex gap-1 items-center text-[14px]">
                  <p className="text-[14px] mb-1 leading-5 text-[#000000]">
                    Project Name
                  </p>
                  <p className="leading-5 text-[#F04438]">*</p>
                </div>
                <InputComponent
                  placeholder="What's the name of the project?"
                  name="name"
                  handleChange={handleChange}
                  type="text"
                  error={error.name}
                  className="w-full h-10 md:h-12 placeholder:text-[16px] placeholder:text-[#98A2B3] rounded-lg border-[#D0D5DD]"
                />
              </div>

              <div className="mt-5 md:mt-0 w-full">
                <div className="flex gap-1 items-center text-[14px]">
                  <p className="text-[14px] mb-1 leading-5 text-[#000000]">
                    Client
                  </p>
                  <p className="leading-5 text-[#F04438]">*</p>
                </div>
                <div
                  className={`w-full cursor-pointer  px-3 flex justify-between items-center h-10 md:h-12 placeholder:text-[16px] border text-[#98A2B3] rounded-lg border-[#D0D5DD] ${error.customerId && "border-red-500"} ${formData.customerId ? "bg-[#F9FAFB]" : "bg-white"}`}
                >
                  <button
                    type="button"
                    onClick={() => {
                      if (selectedProjectCustomer) {
                        showToast(
                          "You can't change the clients on this project.",
                          "info",
                        );
                      } else {
                        setIsOpen(true);
                      }
                    }}
                    className="flex-1 text-left"
                  >
                    {selectedProjectCustomer ? (
                      <span className="text-black capitalize">
                        {selectedProjectCustomer.businessName ||
                          `${selectedProjectCustomer.firstName} ${selectedProjectCustomer.lastName}`}
                      </span>
                    ) : (
                      <>
                        {selectedCustomer ? (
                          <span className="text-black capitalize">
                            {" "}
                            {selectedCustomer.businessName ||
                              `${selectedCustomer.firstName} ${selectedCustomer.lastName}`}
                          </span>
                        ) : (
                          "Who's the project for?"
                        )}
                      </>
                    )}
                  </button>

                  {formData.customerId && (
                    <button
                      type="button"
                      onClick={() => {
                        if (selectedProjectCustomer) {
                          showToast(
                            "You can't change the clients on this project.",
                            "info",
                          );
                        } else {
                          setFormData({ ...formData, customerId: "" });
                          setSelectedCustomer(null);
                          setError({ ...error, customerId: "" });
                        }
                      }}
                    >
                      {" "}
                      <svg
                        width={20}
                        height={20}
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M15 5L5 15M5 5l10 10"
                          stroke="#D92D20"
                          strokeWidth={2}
                          strokeLinecap="square"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  )}
                </div>
                <p className="text-[red] absolute whitespace-nowrap text-[12px]">
                  {error.customerId}
                </p>
              </div>

              <div className="grid md:grid-cols-2 grid-cols-1 gap-5">
                <div className="mt-5 md:mt-0 w-full">
                  <div className="flex gap-1 items-center text-[14px]">
                    <p className="text-[14px] mb-1 leading-5 text-[#000000]">
                      Start Date
                    </p>
                  </div>
                  <InputComponent
                    placeholder="Who is the project for?"
                    name="startDate"
                    handleChange={handleChange}
                    value={formData.startDate}
                    type="date"
                    error={error.startDate}
                    className="w-full h-10 md:h-12 placeholder:text-[16px] placeholder:text-[#98A2B3] rounded-lg border-[#D0D5DD]"
                  />
                </div>
                <div className="mt-5 md:mt-0 w-full">
                  <div className="flex gap-1 items-center text-[14px]">
                    <p className="text-[14px] mb-1 leading-5 text-[#000000]">
                      End Date
                    </p>
                  </div>
                  <InputComponent
                    name="endDate"
                    handleChange={handleChange}
                    value={formData.endDate}
                    type="date"
                    error={error.endDate}
                    className="w-full h-10 md:h-12 placeholder:text-[16px] placeholder:text-[#98A2B3] rounded-lg border-[#D0D5DD]"
                  />
                </div>
              </div>

              <div className="mt-5 md:mt-0 w-full">
                <div className="flex gap-1 items-center text-[14px]">
                  <p className="text-[14px] mb-1 leading-5 text-[#000000]">
                    Project Status
                  </p>
                </div>
                <select
                  value={formData.status} // bind the value
                  onChange={handleChange}
                  className="w-full h-10 md:h-12 placeholder:text-[16px] placeholder:text-[#98A2B3] rounded-lg border-[#D0D5DD]"
                  name="status"
                >
                  <option value="not-started">Not Started</option>
                  <option value="in-progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div className="mt-5 md:mt-0 w-full">
                <div className="flex gap-1 items-center text-[14px]">
                  <p className="text-[14px] mb-1 leading-5 text-[#000000]">
                    Notes
                  </p>
                </div>
                <textarea
                  placeholder="Add more detals about the project."
                  rows={5}
                  cols={3}
                  name="notes"
                  onChange={(e: any) => handleChange(e)}
                  className="w-full  h-[80px] md:h-[140px] placeholder:text-[16px] placeholder:text-[#98A2B3] rounded-lg border-[#D0D5DD]"
                ></textarea>
              </div>
            </form>
          </div>
          {/* Buttons */}
          <div className="w-full gap-[5%] justify-end flex items-center mt-8 md:mt-10 pb-[20px] md:pb-0">
            <button
              onClick={onClose}
              type="button"
              className={`text-[#044E97] h-12 px-5 rounded-md font-semibold border border-gray-300 hover:bg-slate-100`}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={isLoading}
              onClick={handleCreateProject}
              className={`text-[#ffffff] bg-[#044E97] h-12 px-5 rounded-md font-semibold`}
            >
              {isLoading ? "Creating..." : "Create"}
            </button>
          </div>
        </div>
      </div>

      {isOpen && modalInView === "select_customer" && (
        <SelectCustomer
          setModalInView={setModalInView}
          setIsOpen={setIsOpen}
          handleSetCustomer={(selectedCustomer) => {
            handleSetCustomerWrapper(selectedCustomer);
            setSelectedCustomer(selectedCustomer);
            setIsOpen(false);
          }}
          formDataCustomer={""}
        />
      )}
      <div className="md:hidden">
        {isOpen && modalInView === "add_customer" && (
          <AddNewCustomer
            setIsOpen={setIsOpen}
            setFormData={setCustomerFormData}
            handleSetCustomer={(newCustomer) => {
              handleSetCustomerWrapper(newCustomer);
              setSelectedCustomer(newCustomer);
              setIsOpen(false);
            }}
            onClose={handleClose}
          />
        )}
      </div>
      <div className="hidden md:block">
        {isOpen && modalInView === "add_customer" && (
          <AddNewCustomerDesktop
            setIsOpen={setIsOpen}
            setFormData={setCustomerFormData}
            handleSetCustomer={(newCustomer) => {
              handleSetCustomerWrapper(newCustomer);
              setSelectedCustomer(newCustomer);
              setIsOpen(false);
            }}
            onClose={handleClose}
          />
        )}
      </div>
    </>
  );
};

export { CreateProject };
